/**
 * Reloj inyectable. Ninguna regla de negocio llama a `Date.now()` directamente:
 * eso hace las pruebas deterministas y permite simular el cruce de medianoche.
 *
 * Convenciones de tiempo del sistema:
 *  - Los instantes se guardan SIEMPRE en UTC, en ISO-8601 con milisegundos y sufijo `Z`
 *    (formato ordenable lexicograficamente: comparar strings equivale a comparar fechas).
 *  - Las reglas diarias (numeracion, reinicio) usan la FECHA OPERATIVA LOCAL de la
 *    institucion, que se guarda aparte como `YYYY-MM-DD`.
 */

/** Instante UTC en ISO-8601: `2026-08-11T14:03:22.145Z`. */
export type Instant = string;

/** Fecha operativa local de la institucion: `2026-08-11`. */
export type OperationalDate = string;

export interface Clock {
  /** Instante actual en UTC. */
  now(): Instant;
}

export const systemClock: Clock = {
  now: () => new Date().toISOString(),
};

/** Reloj controlable para pruebas. */
export function fixedClock(start: Instant | Date): Clock & {
  set(next: Instant | Date): void;
  advance(ms: number): void;
} {
  let current = toInstant(start);
  return {
    now: () => current,
    set(next) {
      current = toInstant(next);
    },
    advance(ms: number) {
      current = new Date(new Date(current).getTime() + ms).toISOString();
    },
  };
}

const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function toInstant(value: Instant | Date | number): Instant {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`Instante invalido: ${String(value)}`);
  }
  return date.toISOString();
}

export function isInstant(value: unknown): value is Instant {
  return typeof value === 'string' && INSTANT_PATTERN.test(value);
}

export function isOperationalDate(value: unknown): value is OperationalDate {
  return typeof value === 'string' && DATE_PATTERN.test(value) && !Number.isNaN(Date.parse(value));
}

/**
 * Convierte un instante UTC a la fecha operativa local de la institucion.
 *
 * Usa `Intl.DateTimeFormat` con la zona IANA, asi que respeta cambios de horario
 * de verano sin tablas propias. `America/Guatemala` es UTC-6 fijo, pero el sistema
 * no asume eso: otra institucion puede configurar otra zona.
 */
export function operationalDate(instant: Instant, timeZone: string): OperationalDate {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // 'en-CA' produce directamente YYYY-MM-DD.
  return formatter.format(new Date(instant));
}

/** Dia del mes (1-31) de una fecha operativa. */
export function dayOfMonth(date: OperationalDate): number {
  return Number(date.slice(8, 10));
}

/** Suma dias a una fecha operativa sin pasar por zonas horarias. */
export function addDays(date: OperationalDate, days: number): OperationalDate {
  const utc = new Date(`${date}T00:00:00.000Z`);
  utc.setUTCDate(utc.getUTCDate() + days);
  return utc.toISOString().slice(0, 10);
}

/** Diferencia en milisegundos entre dos instantes (`b - a`). */
export function diffMs(a: Instant, b: Instant): number {
  return new Date(b).getTime() - new Date(a).getTime();
}

/** `true` si `instant` ya paso respecto de `reference`. */
export function isExpired(instant: Instant, reference: Instant): boolean {
  return new Date(instant).getTime() <= new Date(reference).getTime();
}

/** Devuelve el instante resultante de sumar segundos. */
export function plusSeconds(instant: Instant, seconds: number): Instant {
  return new Date(new Date(instant).getTime() + seconds * 1000).toISOString();
}

/**
 * Formatea un instante para mostrarlo al usuario en la zona de la institucion.
 * Determinista: se usa el mismo helper en pantalla, PDF y Excel.
 */
export function formatInstant(
  instant: Instant,
  timeZone: string,
  options: { withTime?: boolean } = {},
): string {
  const { withTime = true } = options;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instant));

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const day = `${get('day')}/${get('month')}/${get('year')}`;
  if (!withTime) return day;
  // `hour: '2-digit'` con hour12:false puede devolver "24" a medianoche en algunos runtimes.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${day} ${hour}:${get('minute')}:${get('second')}`;
}
